/**
 * One Speedy as a phone (Tim, 3 Nov: "the Speedy card is an iPhone"; the Version 5 card inside it).
 * Used everywhere a Speedy is listed: Browse, Saved, Series, My Speedies and the feed carousels.
 *
 * Inside the phone: the topic colour (`speedy-colours.ts`), the lighter sun in the top corner (always
 * there), the title large, then over a dark fade at the bottom: up to two topic chips, the title,
 * the length and the views. Top left: an optional chip ("Pending review", "Series · 5 Speedies");
 * top right: a ✓ once finished and, where saving is offered, the bookmark. A finished one dims.
 *
 * Preview (A plan §4.2a): with `previewLoops` (or while pointed at, with `hoverPreview`) the muted
 * preview clip plays inside the phone, whole and centred over the colour. It takes a slot in the
 * shared preview pool (`claimPreviewSlot`, capacity 2, never evicting), so at most 2 previews play
 * across the app. Nothing plays with reduced motion, while the player is open, or when the pool is full.
 *
 * `PhoneFrame` (the phone look, `PHONE_FRAME`) is the shell (series stacks and the not-yet-live cards use it); `SpeedyTileSkeleton`
 * is the same shape while loading, so a card appears in place without anything moving.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bookmark, Check, Clock, Eye } from 'lucide-react';
import type { SpeedyCard } from '@/api/speedies';
import { useToggleSpeedySaved } from '@/hooks/use-speedies';
import { claimPreviewSlot, releasePreviewSlot } from '@/lib/scroll-focus';
import { useSpeedyPlayer } from './SpeedyPlayerProvider';
import { speedyClipSrc } from './speedy-media';
import { topicColour } from './speedy-colours';

export function formatSpeedyLength(s: number | null): string | null {
  if (s === null || !Number.isFinite(s)) return null;
  const total = Math.max(0, Math.round(s));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** 1.2k for 1,234. */
export function formatSpeedyViews(n: number): string {
  if (!n) return '0';
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);
}

const PREVIEW_START_MS = 4000;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export type PhoneSize = 'md' | 'sm';

/**
 * The two phone looks (Tim, 3 Nov): grid cards are a current Pro, with thin black bezels, round
 * corners, the Dynamic Island, a titanium rim and side buttons (option B); the small feed cards keep
 * only the island and the corners (option A), because a rim and buttons turn to noise at 100px.
 * The shape (9:16) and width never change; the side buttons sit just outside the box.
 */
export const PHONE_FRAME: Record<PhoneSize, { radius: string; bezel: string; screen: string; island: string; sun: string; rim: string }> = {
  md: {
    radius: 'rounded-[32px]',
    bezel: 'border-4',
    screen: 'rounded-[28px]',
    island: 'top-[9px] h-4 w-14',
    sun: '-left-16 -top-28 h-60 w-60',
    rim: 'shadow-[0_0_0_1.5px_#8f8a82,inset_0_0_0_1px_rgba(255,255,255,0.08),0_10px_26px_rgba(0,0,0,0.4)]',
  },
  sm: {
    radius: 'rounded-[20px]',
    bezel: 'border-[2.5px]',
    screen: 'rounded-[17.5px]',
    island: 'top-[5px] h-2 w-7',
    sun: '-left-12 -top-20 h-40 w-40',
    rim: 'shadow-[0_6px_16px_rgba(0,0,0,0.35)]',
  },
};

/** Where the chips and buttons on the screen sit: under the island. */
export const PHONE_OVERLAY: Record<PhoneSize, { chipTop: string; controlsTop: string }> = {
  md: { chipTop: 'top-[30px]', controlsTop: 'top-[28px]' },
  sm: { chipTop: 'top-[15px]', controlsTop: 'top-[14px]' },
};

const TITANIUM = 'linear-gradient(90deg, #6b665f, #a7a199, #6b665f)';

/** The Pro's side buttons, as fractions of the phone's height, sitting just outside its edge. */
const SIDE_BUTTONS: { side: 'left' | 'right'; top: string; height: string }[] = [
  { side: 'left', top: '19.7%', height: '6.2%' },
  { side: 'left', top: '28.7%', height: '10.1%' },
  { side: 'left', top: '41%', height: '10.1%' },
  { side: 'right', top: '31.5%', height: '15.2%' },
];

function DynamicIsland({ size }: { size: PhoneSize }) {
  return (
    <span
      className={`pointer-events-none absolute left-1/2 z-[2] -translate-x-1/2 rounded-full bg-black shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] ${PHONE_FRAME[size].island}`}
      aria-hidden
    />
  );
}

/** The phone: frame, island, topic colour and the lighter sun. Children sit on the screen. */
export function PhoneFrame({ screen, size = 'md', dim = false, children, className = '' }: {
  screen: string;
  size?: PhoneSize;
  dim?: boolean;
  children?: ReactNode;
  className?: string;
}) {
  const f = PHONE_FRAME[size];
  return (
    <div className={`relative aspect-[9/16] w-full ${className}`}>
      {size === 'md' && SIDE_BUTTONS.map((b, i) => (
        <span
          key={i}
          className={`pointer-events-none absolute w-[2.5px] rounded-sm ${b.side === 'left' ? '-left-[2.5px]' : '-right-[2.5px]'}`}
          style={{ top: b.top, height: b.height, background: TITANIUM }}
          aria-hidden
        />
      ))}
      <div className={`absolute inset-0 overflow-hidden border-[#050505] bg-black ${f.radius} ${f.bezel} ${f.rim}`}>
        <div className={`absolute inset-0 overflow-hidden ${f.screen} ${dim ? 'opacity-75' : ''}`} style={{ background: screen }}>
          <span className={`pointer-events-none absolute rounded-full bg-white/10 ${f.sun}`} aria-hidden />
          {children}
        </div>
        <DynamicIsland size={size} />
      </div>
    </div>
  );
}

/** The loading shape of a card: the same phone, grey, with bars where the words go. */
export function SpeedyTileSkeleton({ size = 'md' }: { size?: PhoneSize }) {
  const f = PHONE_FRAME[size];
  return (
    <div className="relative aspect-[9/16] w-full" aria-hidden>
      <div className={`absolute inset-0 overflow-hidden border-[#050505] bg-black ${f.radius} ${f.bezel}`}>
        <div className={`absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none ${f.screen}`}>
          <div className={`absolute space-y-1.5 ${size === 'sm' ? 'inset-x-2 bottom-2' : 'inset-x-3 bottom-3'}`}>
            <div className="h-3.5 w-12 rounded-full bg-foreground/10" />
            <div className="h-3.5 w-4/5 rounded bg-foreground/10" />
            <div className="h-3 w-1/2 rounded bg-foreground/10" />
          </div>
        </div>
        <DynamicIsland size={size} />
      </div>
    </div>
  );
}

/** The grid every list of Speedies uses: 2 across on a phone, up to 5 on a wide screen. */
export const SPEEDY_GRID = 'grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

/** A grid of card-shaped placeholders, the same size and spacing as the cards that replace them. */
export function SpeedyGridSkeleton({ count = 10, label = 'Loading Speedies' }: { count?: number; label?: string }) {
  return (
    <div className={SPEEDY_GRID} role="status" aria-label={label}>
      {Array.from({ length: count }, (_, i) => <SpeedyTileSkeleton key={i} />)}
    </div>
  );
}

function SaveButton({ speedyId, saved, size }: { speedyId: string; saved: boolean; size: PhoneSize }) {
  const toggle = useToggleSpeedySaved();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggle.mutate({ speedyId, saved: !saved });
      }}
      aria-pressed={saved}
      aria-label={saved ? 'Remove from saved' : 'Save'}
      className={`relative z-[3] flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/60 ${size === 'sm' ? 'h-7 w-7' : 'h-8 w-8'}`}
    >
      <Bookmark className={`${size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${saved ? 'fill-current' : ''}`} aria-hidden />
    </button>
  );
}

interface SpeedyTileProps {
  card: SpeedyCard & { finished_by_me?: boolean; saved_by_me?: boolean };
  onOpen: () => void;
  size?: PhoneSize;
  /** Play the muted preview this many times, then stop. */
  previewLoops?: number;
  /** Bump to play the preview again (a feed row plays its tiles in turn). */
  previewRun?: number;
  /** The preview has finished, or couldn't play (`played` false: no clip, pool full, reduced motion). */
  onPreviewDone?: (played: boolean) => void;
  /** Play the preview while a mouse points at the card. */
  hoverPreview?: boolean;
  /** Offer the bookmark (Browse, Saved, the feed). */
  showSave?: boolean;
  /** A chip top left, e.g. "Pending review". */
  chip?: { label: string; tone: 'pending' | 'plain' } | null;
  className?: string;
  /** What a tap does, for screen readers, when it isn't "Play" (My Speedies: open the insights). */
  ariaLabel?: string;
}

export function SpeedyTile({
  card, onOpen, size = 'md', previewLoops = 0, previewRun = 0, onPreviewDone, hoverPreview = false, showSave = false, chip = null,
  className = '', ariaLabel,
}: SpeedyTileProps) {
  const { isOpen: playerOpen } = useSpeedyPlayer();
  const [pointed, setPointed] = useState(0);
  const loops = pointed ? Number.POSITIVE_INFINITY : previewLoops;
  const run = previewRun + pointed;
  const [previewing, setPreviewing] = useState(false);
  // The colour stays until the clip actually paints (a video with no frame is black).
  const [painting, setPainting] = useState(false);
  const previewingRef = useRef(false);
  previewingRef.current = previewing;
  const loopsLeft = useRef(loops);
  const done = useRef(false);
  const holder = useRef(Symbol('speedy-tile'));
  const doneCb = useRef(onPreviewDone);
  doneCb.current = onPreviewDone;
  const lastRun = useRef(run);

  useEffect(() => {
    const id = holder.current;
    if (lastRun.current !== run) {
      lastRun.current = run;
      done.current = false;
    }
    if (done.current || loops <= 0) return;
    if (!card.preview_clip_url || playerOpen || prefersReducedMotion()) {
      doneCb.current?.(false);
      return;
    }
    const evicted = () => {
      setPreviewing(false);
      setPainting(false);
      doneCb.current?.(false);
    };
    if (!claimPreviewSlot(id, evicted, { capacity: 2, evict: false })) {
      doneCb.current?.(false);
      return;
    }
    loopsLeft.current = loops;
    setPreviewing(true);
    return () => {
      releasePreviewSlot(id);
      setPreviewing(false);
      setPainting(false);
    };
  }, [loops, run, card.preview_clip_url, playerOpen]);

  // A preview that hasn't started painting within this time gives up its slot (Low Power Mode and
  // data savers block even muted autoplay, with no error).
  useEffect(() => {
    if (!previewing || painting) return;
    const t = setTimeout(() => finishPreview(false), PREVIEW_START_MS);
    return () => clearTimeout(t);
    // finishPreview is stable in effect: it only touches refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewing, painting]);

  const finishPreview = (played = true) => {
    // Late events from a video already taken down do nothing.
    if (done.current && !previewingRef.current) return;
    previewingRef.current = false;
    done.current = true;
    releasePreviewSlot(holder.current);
    setPreviewing(false);
    setPainting(false);
    doneCb.current?.(played);
  };

  const length = formatSpeedyLength(card.duration_s);
  const title = card.title ?? 'Untitled Speedy';
  const sm = size === 'sm';
  const tags = [card.group_label, ...(card.shown_tags ?? []).map((t) => t.label)]
    .filter((t, i, all): t is string => !!t && all.findIndex((x) => x?.toLowerCase() === t.toLowerCase()) === i)
    .slice(0, sm ? 1 : 2);

  return (
    <div
      className={`group relative w-full ${className}`}
      onPointerEnter={(e) => { if (hoverPreview && e.pointerType === 'mouse') setPointed((n) => n + 1); }}
      onPointerLeave={(e) => { if (hoverPreview && e.pointerType === 'mouse') setPointed(0); }}
    >
      <PhoneFrame screen={topicColour(card.group_key).screen} size={size} dim={!!card.finished_by_me}>
        {!(previewing && painting) && (
          <p className={`absolute font-extrabold leading-[1.12] tracking-[-0.01em] text-white ${sm ? 'inset-x-2.5 top-[30%] line-clamp-4 text-[13px]' : 'inset-x-4 top-[32%] line-clamp-4 text-xl'}`} aria-hidden>
            {title}
          </p>
        )}
        {previewing && card.preview_clip_url && (
          <video
            ref={(el) => {
              if (!el || el.dataset.started) return;
              // Set on the element itself before playing: some browsers ignore the muted attribute
              // React renders, and only a muted, inline video may play on its own.
              el.dataset.started = '1';
              el.muted = true;
              el.defaultMuted = true;
              el.playsInline = true;
              void el.play().catch(() => finishPreview(false));
            }}
            src={speedyClipSrc(card)}
            muted
            playsInline
            preload="auto"
            aria-hidden
            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ${painting ? 'opacity-100' : 'opacity-0'}`}
            onPlaying={() => setPainting(true)}
            onEnded={(e) => {
              loopsLeft.current -= 1;
              if (loopsLeft.current > 0) void e.currentTarget.play().catch(() => finishPreview(false));
              else finishPreview();
            }}
            onError={() => finishPreview(false)}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" aria-hidden />
        {chip && (
          <span className={`absolute left-2 rounded-full font-bold text-white ${PHONE_OVERLAY[size].chipTop} ${sm ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[11px]'} ${chip.tone === 'pending' ? 'bg-[rgba(214,160,64,0.92)]' : 'bg-black/50'}`}>
            {chip.label}
          </span>
        )}
        <div className={`absolute inset-x-0 bottom-0 flex flex-col text-white ${sm ? 'gap-1 p-2' : 'gap-1.5 p-3'}`}>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => (
                <span key={t} className={`truncate rounded-full bg-white/15 font-semibold text-white/90 ${sm ? 'max-w-full px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[11px]'}`}>{t}</span>
              ))}
            </div>
          )}
          <p className={`font-semibold leading-tight ${sm ? 'line-clamp-2 text-xs' : 'line-clamp-2 text-[15px] leading-5'}`}>{title}</p>
          <p className={`flex items-center gap-2.5 tabular-nums text-white/70 ${sm ? 'text-[10px]' : 'text-xs'}`}>
            {length && <span className="flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden />{length}</span>}
            {card.view_count > 0 && <span className="flex items-center gap-1"><Eye className="h-3 w-3" aria-hidden />{formatSpeedyViews(card.view_count)}</span>}
          </p>
        </div>
      </PhoneFrame>
      {/* The tap target covers the phone; the bookmark sits above it. */}
      <button
        type="button"
        onClick={onOpen}
        className={`absolute inset-0 z-[1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${PHONE_FRAME[size].radius}`}
        aria-label={ariaLabel ?? `Play ${title}${length ? `, ${length}` : ''}${card.finished_by_me ? ', watched' : ''}`}
      />
      <div className={`absolute flex items-center gap-1.5 ${PHONE_OVERLAY[size].controlsTop} ${sm ? 'right-1.5' : 'right-2.5'}`}>
        {card.finished_by_me && (
          <span className={`relative z-[2] flex items-center justify-center rounded-full bg-[#3F9F62] ${sm ? 'h-5 w-5' : 'h-6 w-6'}`} aria-hidden>
            <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
          </span>
        )}
        {showSave && card.saved_by_me !== undefined && <SaveButton speedyId={card.id} saved={card.saved_by_me} size={size} />}
      </div>
    </div>
  );
}
